import { getIcon } from './icons.js';

/**
 * Data-driven toolbar component.
 *
 * Usage:
 *   new AppToolbar(container, {
 *       groups: [
 *           { id: 'project', items: [
 *               { id: 'openProject', icon: 'folder', title: 'Open Project' },
 *               { type: 'label', id: 'projectName' },
 *           ]},
 *           { type: 'spacer' },
 *           { id: 'info', items: [{ type: 'label', id: 'taskCount' }] },
 *       ],
 *       onButtonClick: (buttonId) => { ... },
 *       collapsible: true,
 *   })
 */
export class AppToolbar {
    constructor(container, options = {}) {
        this._container = container;
        this._options = options;
        this._collapsed = false;
        this._buttons = new Map();
        this._labels = new Map();
        this._groups = new Map();
        this._render();
    }

    /** Get a button DOM element by id (for enable/disable). */
    getButton(id) {
        return this._buttons.get(id) || null;
    }

    /** Get a toolbar group DOM element by id (for show/hide). */
    getGroup(id) {
        return this._groups.get(id) || null;
    }

    /** Update a label's text content by id. */
    setLabel(id, text) {
        const el = this._labels.get(id);
        if (el) el.textContent = text;
    }

    /** Whether the toolbar is currently collapsed. */
    isCollapsed() {
        return this._collapsed;
    }

    /** Collapse the toolbar (and header). */
    collapse() {
        if (!this._collapsed) this.toggle();
    }

    /** Expand the toolbar (and header). */
    expand() {
        if (this._collapsed) this.toggle();
    }

    /** Toggle collapsed state. */
    toggle() {
        this._collapsed = !this._collapsed;
        this._toolbarRow.classList.toggle('nm-toolbar-hidden', this._collapsed);
        const appHeader = document.querySelector('header');
        if (appHeader) appHeader.classList.toggle('nm-header-hidden', this._collapsed);
    }

    _render() {
        const toolbar = document.createElement('div');
        toolbar.className = 'nm-toolbar';
        this._toolbarRow = toolbar;

        const groups = this._options.groups || [];
        for (const group of groups) {
            if (group.type === 'spacer') {
                const spacer = document.createElement('div');
                spacer.className = 'nm-toolbar-spacer';
                toolbar.appendChild(spacer);
                continue;
            }

            const groupEl = document.createElement('div');
            groupEl.className = 'nm-toolbar-group';
            if (group.id) this._groups.set(group.id, groupEl);

            const items = group.items || [];
            for (const item of items) {
                if (item.type === 'label') {
                    const span = document.createElement('span');
                    span.className = 'nm-toolbar-label';
                    if (item.className) span.classList.add(item.className);
                    this._labels.set(item.id, span);
                    groupEl.appendChild(span);
                } else {
                    // Default: button
                    const btn = document.createElement('button');
                    btn.className = 'nm-toolbar-btn';
                    btn.id = item.id || '';
                    btn.title = item.title || '';
                    btn.innerHTML = getIcon(item.icon);
                    btn.addEventListener('click', () => {
                        this._options.onButtonClick?.(item.id);
                    });
                    this._buttons.set(item.id, btn);
                    groupEl.appendChild(btn);
                }
            }

            toolbar.appendChild(groupEl);
        }

        this._container.appendChild(toolbar);
    }
}
